/* ----------------------------------------------------------------------------
   ProShip rate-card pricing model — the EXACT 19-column courier-pricing upload
   format (verbatim from mockup m0.js / COURIER_PRICING_SAMPLE (2).csv). One row
   per Zone × weight slab. `csv` headers must NOT be renamed.
   Also: TAT_ZONES canon (shared by onboarding TAT step + Update Rates & TAT).
   ---------------------------------------------------------------------------- */

/* zone canon — shared by the onboarding TAT step and the Update Rates & TAT wizard */
export const TAT_ZONES = [
  {key: 'local', n: 1, name: 'LOCAL', desc: 'Within city / Local pincodes', std: [1, 1]},
  {key: 'regional', n: 2, name: 'REGIONAL', desc: 'Within state / Regional', std: [1, 1]},
  {key: 'metro', n: 3, name: 'METRO', desc: 'Metro to Metro pairs', std: [1, 2]},
  {key: 'roi', n: 4, name: 'ROI', desc: 'Rest of India', std: [2, 3]},
  {key: 'special', n: 5, name: 'SPECIAL', desc: 'Special zones · NE · J&K · Islands', std: [3, 4]},
]

export const COD_SLABS_DEFAULT =
  '[{"minOrderValue":0,"maxOrderValue":500,"chargeType":"PERCENTAGE","chargeValue":2.0},{"minOrderValue":500,"maxOrderValue":1500,"chargeType":"PERCENTAGE","chargeValue":1.0},{"minOrderValue":1500,"maxOrderValue":null,"chargeType":"FLAT","chargeValue":30}]'

/* The 19 ProShip pricing columns. `key` = data field, `hd` = grid header,
   `csv` = the EXACT upload header, `kind` drives the cell editor. */
export const PRICING_COLS = [
  {key: 'zone', hd: 'ZONE', csv: 'Zone', kind: 'zone'},
  {key: 'start', hd: 'START', csv: 'Start Weight(gm)', kind: 'num'},
  {key: 'min', hd: 'MIN', csv: 'Min Weight(gm)', kind: 'num'},
  {key: 'max', hd: 'MAX', csv: 'Max Weight(gm)', kind: 'num'},
  {key: 'price', hd: 'PRICE ₹', csv: 'Price', kind: 'num', primary: true},
  {key: 'addUnit', hd: '+UNIT', csv: 'Additional Unit(gm)', kind: 'num'},
  {key: 'addRate', hd: '+RATE', csv: 'Additional Unit Rate', kind: 'num'},
  {key: 'vc', hd: 'VC', csv: 'Volumetric Coefficient', kind: 'num'},
  {key: 'tax', hd: 'TAX%', csv: 'Tax(%)', kind: 'num'},
  {key: 'fsc', hd: 'FSC%', csv: 'Fuel Surcharge(%)', kind: 'num'},
  {key: 'docket', hd: 'DOCKET', csv: 'Docket Charge', kind: 'num'},
  {key: 'codPct', hd: 'COD%', csv: 'Invoice Percentage for COD(Optional)%', kind: 'num'},
  {key: 'codOp', hd: 'OP', csv: 'COD Operator(Min/Max)(Optional)', kind: 'op'},
  {key: 'codFixed', hd: 'COD ₹', csv: 'Fixed COD Charge(Optional)', kind: 'num'},
  {key: 'rvpNoQc', hd: 'RVP NQC', csv: 'RVP Without QC', kind: 'num'},
  {key: 'rvpQc', hd: 'RVP QC', csv: 'RVP With QC', kind: 'num'},
  {key: 'rto', hd: 'RTO%', csv: 'RTO (Forward %)', kind: 'num'},
  {key: 'codType', hd: 'COD TYPE', csv: 'COD Charge Type', kind: 'type'},
  {key: 'codSlabs', hd: 'COD SLABS', csv: 'COD Slabs', kind: 'json'},
]

export const ZONE_LABEL = {1: 'Local', 2: 'Regional', 3: 'Metro', 4: 'ROI', 5: 'Special'}

export function proshipRow(zone, lo, hi, price, basePrice, ex) {
  ex = ex || {}
  return {
    zone: zone,
    start: lo,
    min: lo,
    max: hi,
    price: price,
    basePrice: basePrice === undefined ? null : basePrice,
    addUnit: ex.addUnit != null ? ex.addUnit : 0,
    addRate: ex.addRate != null ? ex.addRate : 0,
    vc: ex.vc != null ? ex.vc : 5000,
    tax: ex.tax != null ? ex.tax : 18,
    fsc: ex.fsc != null ? ex.fsc : 0,
    docket: ex.docket != null ? ex.docket : 0,
    codPct: ex.codPct != null ? ex.codPct : 2,
    codOp: ex.codOp || 'MAX',
    codFixed: ex.codFixed != null ? ex.codFixed : 33,
    rvpNoQc: ex.rvpNoQc != null ? ex.rvpNoQc : 0,
    rvpQc: ex.rvpQc != null ? ex.rvpQc : 0,
    rto: ex.rto != null ? ex.rto : 90,
    codType: ex.codType || 'VARIABLE',
    codSlabs: ex.codSlabs || COD_SLABS_DEFAULT,
  }
}

/* 5 zones × 12 weight slabs (0–6000g) — the matrix shape shown in the reviewed preview. */
export function pricingSeed(opts) {
  opts = opts || {}
  const ptl = !!opts.ptl
  const withBase = !!opts.withBase
  const rows = []
  for (let z = 1; z <= 5; z++) {
    for (let i = 0; i < 12; i++) {
      const lo = i * 500
      const hi = (i + 1) * 500
      const price = Math.round((ptl ? 180 : 24) + (z - 1) * (ptl ? 40 : 8) + i * (ptl ? 22 : 6))
      rows.push(
        proshipRow(z, lo, hi, price, withBase ? price : null, {
          rvpNoQc: 84 + (z - 1) * 10 + (i % 3) * 22,
          rvpQc: 149 + (z - 1) * 10 + (i % 3) * 22,
        })
      )
    }
  }
  return rows
}

export function csvCell(v) {
  v = v == null ? '' : String(v)
  return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
}

export function pricingCsv(rows, priceFromBase) {
  const hd = PRICING_COLS.map((c) => c.csv).join(',')
  const body = (rows || [])
    .map((r) =>
      PRICING_COLS.map((c) => {
        const v = c.key === 'price' && priceFromBase ? (r.basePrice != null ? r.basePrice : r.price) : r[c.key]
        return csvCell(v)
      }).join(',')
    )
    .join('\n')
  return hd + '\n' + body + '\n'
}
